import { useCallback, useEffect, useRef, useState } from "react";
import { memberRouter } from "../../app/runtime";
import { authApi } from "../shared/api";
import { navigate } from "../shared/ui";

interface Destination { readonly to: string; readonly replace?: boolean }

/** An unfinished identity flow belongs to this visit, including its PIN setup stage. */
export function useIdentityExitGuard(storageKey: string, onPageExit: () => void) {
  const abandonedKey = `${storageKey}.abandoned`;
  const staleTokens = useRef([...new Set([
    sessionStorage.getItem(storageKey), sessionStorage.getItem(`${storageKey}.requestKey`),
    sessionStorage.getItem(abandonedKey),
  ].filter((value): value is string => Boolean(value)))]);
  const activeToken = useRef<string | undefined>(undefined);
  const dirty = useRef(false);
  const finished = useRef(false);
  const mounted = useRef(false);
  const leaving = useRef(false);
  const pageExit = useRef(onPageExit);
  const abandonRequest = useRef(authApi.abandonIdentity);
  pageExit.current = onPageExit;
  const currentUrl = useRef(`${window.location.pathname}${window.location.search}${window.location.hash}`);
  const [initializing, setInitializing] = useState(staleTokens.current.length > 0);
  const [destination, setDestination] = useState<Destination>();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();

  const abandon = useCallback(async (token: string) => {
    sessionStorage.setItem(abandonedKey, token);
    await abandonRequest.current(token);
    if (sessionStorage.getItem(abandonedKey) === token) sessionStorage.removeItem(abandonedKey);
  }, [abandonedKey]);

  const clearStorage = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(`${storageKey}.requestKey`);
  }, [storageKey]);

  const restart = useCallback(async () => {
    setInitializing(true); setError(undefined); clearStorage();
    try {
      for (const token of staleTokens.current) await abandon(token);
      staleTokens.current = [];
      if (mounted.current) setInitializing(false);
    } catch {
      if (mounted.current) setError("이전 본인인증을 종료하지 못했습니다. 다시 시도해 주세요.");
    }
  }, [abandon, clearStorage]);

  useEffect(() => {
    mounted.current = true;
    void restart();
    const dispose = () => {
      clearStorage();
      if (!finished.current && activeToken.current) void abandon(activeToken.current).catch(() => undefined);
      activeToken.current = undefined; dirty.current = false;
    };
    const unblock = memberRouter.blockNavigation((to, options) => {
      if (!dirty.current || finished.current || to === currentUrl.current) return true;
      setError(undefined); setDestination({ to, replace: options.replace });
      return false;
    });
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty.current || finished.current) return;
      event.preventDefault(); event.returnValue = "";
    };
    const onPageHide = () => { dispose(); pageExit.current(); };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      mounted.current = false;
      unblock();
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
      // React StrictMode's effect probe immediately mounts again; it is not a page exit.
      queueMicrotask(() => { if (!mounted.current) dispose(); });
    };
  }, [abandon, clearStorage, restart]);

  const track = useCallback((token: string, expectedRequestKey?: string) => {
    if (!mounted.current || leaving.current || (expectedRequestKey && activeToken.current !== expectedRequestKey)) {
      void abandon(token).catch(() => undefined); return false;
    }
    activeToken.current = token; dirty.current = true;
    return true;
  }, [abandon]);
  const markDirty = useCallback((value: boolean) => { if (value) dirty.current = true; }, []);
  const finish = useCallback(() => { finished.current = true; dirty.current = false; clearStorage(); }, [clearStorage]);
  const abandonCurrent = useCallback(async () => {
    if (activeToken.current) await abandon(activeToken.current);
    activeToken.current = undefined;
    clearStorage(); dirty.current = false; finished.current = false;
  }, [abandon, clearStorage]);
  const stay = () => { if (!leaving.current) { setDestination(undefined); setError(undefined); } };
  const leave = async () => {
    if (!destination || leaving.current) return;
    leaving.current = true;
    setPending(true); setError(undefined);
    try {
      if (activeToken.current) await abandon(activeToken.current);
      activeToken.current = undefined; clearStorage(); dirty.current = false;
      pageExit.current();
      navigate(destination.to, destination.replace);
      if (mounted.current) setDestination(undefined);
    } catch {
      if (mounted.current) setError("본인인증을 종료하지 못했습니다. 다시 시도해 주세요.");
    } finally { leaving.current = false; if (mounted.current) setPending(false); }
  };
  return { initializing, restart, track, markDirty, finish, abandonCurrent, destination, pending, error, stay, leave };
}
